import React, { useState, useEffect, useRef } from 'react';
import { Search, Clock, X } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useSearchHistory } from '../hooks/useSearchHistory';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (query: string) => void;
  placeholder?: string;
  isLoading?: boolean; // Add loading prop
}

interface SearchHistoryItem {
  search_query: string;
  created_at: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = "Search manhwa recaps by description, channel, or tags...",
  isLoading = false // Default to false
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const { user } = useAuth();
  const { loadSearchHistory, clearSearchHistory: clearHistory, removeSearchFromHistory } = useSearchHistory();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if current value is a YouTube video ID
  const isYouTubeVideoId = /^[A-Za-z0-9_-]{11}$/.test(value);

  // Load search history when component mounts
  useEffect(() => {
    loadSearchHistoryFromAPI();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSearchHistoryFromAPI = async () => {
    console.log('Loading search history from localStorage');
    setIsLoadingHistory(true);
    try {
      const history = await loadSearchHistory(10);
      console.log('Search history loaded:', history);
      setSearchHistory(history);
    } catch (error) {
      console.error('Error loading search history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSearch = (query: string) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery) {
      console.log('SearchBar: Performing search:', trimmedQuery); // Debug log
      onSearch?.(trimmedQuery);
    }
    setShowHistory(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Show history dropdown when input is focused and has no value
    if (newValue === '') {
      console.log('Showing search history dropdown');
      setShowHistory(true);
      loadSearchHistoryFromAPI(); // Reload history when showing dropdown
    } else {
      setShowHistory(false);
    }
  };

  const handleInputFocus = () => {
    if (value === '') {
      console.log('Input focused, showing search history');
      setShowHistory(true);
      loadSearchHistoryFromAPI(); // Reload history when focusing
    }
  };

  const handleHistoryItemClick = (query: string) => {
    onChange(query);
    handleSearch(query);
    inputRef.current?.blur();
  };

  // Save search when user presses Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(value);
    }
  };

  const clearSearchHistory = async () => {
    if (!user) return;

    try {
      const success = await clearHistory();
      if (success) {
        setSearchHistory([]);
        setShowHistory(false);
      }
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  };

  const handleRemoveSearch = async (queryToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the search

    try {
      const success = await removeSearchFromHistory(queryToRemove);
      if (success) {
        // Update the local state to reflect the removal
        setSearchHistory(prev => prev.filter(item => item.search_query !== queryToRemove));
      }
    } catch (error) {
      console.error('Error removing search:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <div className="relative w-full max-w-lg sm:max-w-2xl" ref={dropdownRef}>
      {/* Search Icon or Loading Spinner */}
      {isLoading ? (
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-5 h-5" />
      )}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={isLoading ? "Searching..." : placeholder}
        className={`w-full pl-12 ${value ? 'pr-12' : 'pr-4'} py-3 bg-white dark:bg-gray-800 border rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${isYouTubeVideoId
            ? 'border-blue-500 focus:ring-blue-500'
            : isLoading
              ? 'border-purple-400 focus:ring-purple-400 animate-pulse'
              : 'border-gray-300 dark:border-gray-700 focus:ring-purple-500'
          }`}
      />

      {/* Clear Button - Only show when there's text */}
      {value && !isLoading && (
        <button
          onClick={() => {
            onChange(''); // Clear the search value
            inputRef.current?.focus(); // Keep focus on input
          }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          title="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* YouTube Video ID Indicator */}
      {isYouTubeVideoId && (
        <div className={`absolute top-1/2 transform -translate-y-1/2 flex items-center space-x-2 ${
          value ? 'right-12' : 'right-4'
        }`}>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Video ID</span>
        </div>
      )}

      {/* Search History Dropdown */}
      {showHistory && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Recent Searches
              </h3>
              {searchHistory.length > 0 && (
                <button
                  onClick={clearSearchHistory}
                  className="text-xs text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {isLoadingHistory ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : searchHistory.length > 0 ? (
            <div className="py-2">
              {searchHistory.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleHistoryItemClick(item.search_query)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center justify-between group"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <Clock className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-900 dark:text-white truncate">
                      {item.search_query}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {formatDate(item.created_at)}
                    </span>
                    <button
                      onClick={(e) => handleRemoveSearch(item.search_query, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      title="Remove from history"
                    >
                      <X className="w-3 h-3 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400" />
                    </button>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              No recent searches
            </div>
          )}
        </div>
      )}
    </div>
  );
};